import { useState, useEffect } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { StatusBadge } from "../../../common/Badge";
import loadingSpinner from "../../../assets/loading.gif";

export const ViewSalaryAdvanceModal = ({ isOpen, onClose, requestData }) => {
  const [loading, setLoading] = useState(false);

  // Reset loading when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setLoading(false);
    }
  }, [isOpen]);

  if (!isOpen || !requestData) return null;

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "—";
    const mm = date.getMonth() + 1;
    const dd = date.getDate();
    const yyyy = date.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "—";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "—";
    const mm = date.getMonth() + 1;
    const dd = date.getDate();
    const yyyy = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");
    return `${mm}/${dd}/${yyyy} ${hours}:${minutes}`;
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return "—";
    return new Intl.NumberFormat("en-PH", {
      style: "currency",
      currency: "PHP",
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const getApproverStatusClass = (status) => {
    switch (status?.toLowerCase()) {
      case "approved":
        return "bg-green-100 text-green-800";
      case "disapproved":
        return "bg-red-100 text-red-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-gray-100 text-gray-600";
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[10000] bg-black/50 flex items-center justify-center p-3 text-gray-700 overflow-auto"
      onClick={onClose}
    >
      <div
        className="bg-gray-50 w-full md:w-[90%] lg:w-[60%] xl:w-[45%] rounded-lg shadow-xl h-auto max-h-[95vh] flex flex-col relative"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex justify-between items-center px-6 py-2 border-b border-gray-200 sticky top-0 bg-white z-10 rounded-t-lg shadow-sm">
          <h2 className="text-xl font-semibold text-gray-800 flex items-center space-x-2">
            <span className="text-xl text-gray-600">Salary Advance Request Details</span>
          </h2>

          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700 transition-colors p-1 bg-transparent border-none cursor-pointer"
            aria-label="Close modal"
          >
            <CloseIcon fontSize="medium" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto flex-grow space-y-6">
          {loading ? (
            <div className="flex justify-center items-center py-10">
              <img src={loadingSpinner} alt="loading" className="w-12 h-12" />
            </div>
          ) : (
            <>
              {/* Request Information Section */}
              <div className="border border-gray-300 rounded-lg bg-white px-4 shadow-sm pb-4">
                <div className="py-2 border-b border-gray-300 mb-4">
                  <h3 className="font-medium text-gray-700 uppercase text-sm tracking-wide">
                    Request Information
                  </h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
                  <div className="mb-3">
                    <label className="mb-1 block text-sm font-medium text-gray-500">
                      Reference Number
                    </label>
                    <div className="text-gray-800 font-medium">
                      {requestData.formattedReferenceNumber || requestData.referenceNumber || "N/A"}
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="mb-1 block text-sm font-medium text-gray-500">
                      Status
                    </label>
                    <div>
                      <StatusBadge status={requestData.status} />
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="mb-1 block text-sm font-medium text-gray-500">
                      Request Date
                    </label>
                    <div className="text-gray-800">
                      {formatDate(requestData.requestDate)}
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="mb-1 block text-sm font-medium text-gray-500">
                      Amount Requested
                    </label>
                    <div className="text-gray-800 font-semibold text-blue-600">
                      {formatCurrency(requestData.amount)}
                    </div>
                  </div>

                  <div className="mb-3 md:col-span-2">
                    <label className="mb-1 block text-sm font-medium text-gray-500">
                      Reason
                    </label>
                    <div className="text-gray-800 bg-gray-50 p-3 rounded-md border border-gray-200">
                      {requestData.reason || "No reason provided"}
                    </div>
                  </div>
                </div>
              </div>

              {/* Approver Hierarchy Section */}
              <div className="border border-gray-300 rounded-lg bg-white shadow-sm overflow-hidden">
                <div className="px-4 py-2 border-b border-gray-300 bg-gray-50">
                  <h3 className="font-medium text-gray-700 uppercase text-sm tracking-wide">
                    Approver Hierarchy ({requestData.currentHierarchy || 1} of {requestData.hierarchyCount || 0})
                  </h3>
                </div>

                <div className="p-4">
                  {requestData.approvals && requestData.approvals.length > 0 ? (
                    <div className="space-y-3">
                      {requestData.approvals.map((approver, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border-l-4"
                          style={{
                            borderLeftColor: approver.status === "Approved"
                              ? "#10b981"
                              : approver.status === "Disapproved"
                                ? "#ef4444"
                                : "#f59e0b"
                          }}
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-semibold text-gray-700">
                                Level {approver.hierarchy}
                              </span>
                              <span className="text-gray-500 text-sm">
                                {approver.approverFullName || `Approver ID: ${approver.approverId}`}
                              </span>
                            </div>
                            {approver.remarks && (
                              <div className="text-sm text-gray-500 mt-1">
                                Remarks: {approver.remarks}
                              </div>
                            )}
                            {approver.actionDate && (
                              <div className="text-xs text-gray-400 mt-1">
                                {formatDateTime(approver.actionDate)}
                              </div>
                            )}
                          </div>
                          <div>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getApproverStatusClass(approver.status)}`}>
                              {approver.status || "Pending"}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      No approver information available.
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-2 p-5 border-t border-gray-200 bg-white rounded-b-lg shadow-sm">
          <button
            onClick={onClose}
            className="bg-gray-400 hover:bg-gray-500 text-white px-6 py-2 rounded border-none cursor-pointer transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};